import type { AttackKind } from './simulation'

export type CombatMemorySample={
  t:number
  player:[number,number,number]
  enemy:[number,number,number]
  impactSerial:number
  score:number
  playerAttack:AttackKind|null
  enemyAttack:AttackKind|null
}
export type CombatMemoryReplay={samples:CombatMemorySample[];start:number;end:number}
export type CombatMemoryView={
  active:boolean
  player:[number,number,number]
  enemy:[number,number,number]
  intensity:number
  progress:number
}
export const combatMemoryView:CombatMemoryView={active:false,player:[0,0,0],enemy:[0,0,0],intensity:0,progress:0}
const lerp=(a:number,b:number,t:number)=>a+(b-a)*t
const clamp01=(v:number)=>Math.max(0,Math.min(1,v))
export class CombatMemoryBuffer{
  private samples:CombatMemorySample[]=[]
  constructor(private readonly seconds=8,private readonly maxSamples=192){}
  clear(){this.samples=[]}
  record(sample:CombatMemorySample){
    if(!Number.isFinite(sample.t))return
    this.samples.push(sample)
    const cutoff=sample.t-this.seconds
    while(this.samples.length>0&&(this.samples[0].t<cutoff||this.samples.length>this.maxSamples))this.samples.shift()
  }
  size(){return this.samples.length}
  bestReplay(windowSeconds=6):CombatMemoryReplay|null{
    if(this.samples.length<2)return null
    let best=this.samples[0]
    for(const sample of this.samples)if(sample.score>best.score)best=sample
    const half=Math.max(.4,windowSeconds/2)
    let selected=this.samples.filter(sample=>sample.t>=best.t-half&&sample.t<=best.t+half)
    if(selected.length<2)selected=this.samples.slice()
    return{samples:selected,start:selected[0].t,end:selected[selected.length-1].t}
  }
  sample(replay:CombatMemoryReplay,progress:number):CombatMemorySample|null{
    const samples=replay.samples
    if(!samples.length)return null
    const p=clamp01(progress),target=lerp(replay.start,replay.end,p)
    let right=1
    while(right<samples.length&&samples[right].t<target)right++
    if(right>=samples.length)return samples[samples.length-1]
    const a=samples[Math.max(0,right-1)],b=samples[right],span=Math.max(.0001,b.t-a.t),u=clamp01((target-a.t)/span)
    return{
      t:target,
      player:[lerp(a.player[0],b.player[0],u),lerp(a.player[1],b.player[1],u),lerp(a.player[2],b.player[2],u)],
      enemy:[lerp(a.enemy[0],b.enemy[0],u),lerp(a.enemy[1],b.enemy[1],u),lerp(a.enemy[2],b.enemy[2],u)],
      impactSerial:u<.5?a.impactSerial:b.impactSerial,
      score:lerp(a.score,b.score,u),
      playerAttack:u<.5?a.playerAttack:b.playerAttack,
      enemyAttack:u<.5?a.enemyAttack:b.enemyAttack,
    }
  }
}
